import { FC, ReactNode, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { useInView } from 'react-intersection-observer';
import { AnalyticsCommonTypes } from '@frontend/api-analytics';
import { CallIntelligenceV2, CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';
import { useIsMobile, UserCard } from '@frontend/analytics-shared';
import {
  CallIntelChip,
  CallIntelTooltip,
  generateTrackingId,
  useCallIntelContactTypesLabel,
  useCallIntelDefaultsShallowStore,
} from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { emptyStateGraphics, NakedButton, SkeletonLoaders, Text, TextLink, useAlert } from '@frontend/design-system';
import { URLs } from '../../../constants';
import { useActiveFilters } from '../hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../hooks/use-call-intel-demo-flags';
import { useCallIntelShallowStore } from '../hooks/use-call-intel-store';

const TASK_TYPE_PREFIX = 'TYPE_';

// Mapping TaskTypeEnum to InfoTipIds
const TaskTypeInfoTipMapping: Record<CallIntelTypes.TaskType, CallIntelTypes.InfoTipId> = {
  [CallIntelTypes.TaskType.TYPE_UNKNOWN]: 'taskTypeOther',
  [CallIntelTypes.TaskType.TYPE_SCHEDULING]: 'taskTypeScheduling',
  [CallIntelTypes.TaskType.TYPE_WAITLIST]: 'taskTypeWaitlist',
  [CallIntelTypes.TaskType.TYPE_PATIENT_CARE]: 'taskTypePatientCare',
  [CallIntelTypes.TaskType.TYPE_INSURANCE]: 'taskTypeInsurance',
  [CallIntelTypes.TaskType.TYPE_BILLING]: 'taskTypeBilling',
  [CallIntelTypes.TaskType.TYPE_OTHER]: 'taskTypeOther',
};

const DEFAULT_PAGE_CONFIG: AnalyticsCommonTypes.PageConfig = {
  pageNumber: 1,
  pageSize: 3,
};

//Task Reason Card Component Loader
const TaskReasonCardLoader: FC<{ type: 'header' | 'body' }> = ({ type }) => {
  return (
    <>
      {type === 'header' ? (
        <SkeletonLoaders.Loader height={theme.spacing(5)} />
      ) : (
        <SkeletonLoaders.Loader height={theme.spacing(23)} />
      )}
    </>
  );
};

type TaskReasonCardHeadingProps = {
  background: string;
  color: string;
  count: number;
  infoTip: ReactNode;
  label: string;
  onNavigate: () => void;
};

// Task Reason Card Heading Component
const TaskReasonCardHeading = ({
  background,
  color,
  count,
  label,
  infoTip,
  onNavigate,
}: TaskReasonCardHeadingProps) => {
  const { t } = useTranslation('call-intelligence');
  const { isDemoAccount } = useCallIntelDemoFlags();

  return (
    <div css={styles.taskReasonCardHeadingWrapper(background, color)}>
      <div className='info-wrapper'>
        <Text className='info-count' weight='bold'>
          {count}
        </Text>
        <Text weight='bold'>{label}</Text>
        {infoTip}
      </div>
      <TextLink
        onClick={onNavigate}
        size='medium'
        trackingId={generateTrackingId({
          component: 'follow-up-task-reason-card',
          context: 'view-list-button',
          isDemoAccount,
        })}
        weight='bold'
      >
        {t('View List')}
      </TextLink>
    </div>
  );
};

// Empty Task Component
const EmptyTask: FC<{ typeLabel: string }> = ({ typeLabel }) => {
  const { t } = useTranslation('call-intelligence');

  const EmptyStateGraphicNoTasks = emptyStateGraphics.noCallFollowUps;

  return (
    <div css={styles.emptyStateWrapper}>
      <EmptyStateGraphicNoTasks height={124} width={124} />
      <Text css={styles.emptyStateText}>{t('No {{typeLabel}} Follow-ups', { typeLabel })}</Text>
    </div>
  );
};

type TaskPatientPreviewProps = {
  call: CallIntelTypes.CallRecord;
  contactType?: CallIntelTypes.ContactTypeEnum;
  totalTasks: number;
  onNavigate: (id: string) => void;
};
// Task Patient Preview Component
const TaskPatientPreview = ({
  call,
  contactType = CallIntelTypes.ContactTypeEnum.CONTACT_UNKNOWN,
  onNavigate,
  totalTasks,
}: TaskPatientPreviewProps) => {
  const { t } = useTranslation('analytics');
  const { isDemoAccount } = useCallIntelDemoFlags();
  const { chipVariants } = useCallIntelDefaultsShallowStore('chipVariants');
  const contactTypeLabelMap = useCallIntelContactTypesLabel();

  if (!call) {
    return null;
  }

  return (
    <NakedButton
      css={styles.taskPatientPreviewWrapper}
      trackingId={generateTrackingId({
        component: 'follow-up-task-reason-card',
        context: 'list-item-click',
        isDemoAccount,
      })}
      onClick={() => onNavigate(CallIntelUtils.generateContactId(call))}
    >
      <UserCard
        css={styles.userCard}
        firstName={call.person?.firstName}
        lastName={call.person?.lastName}
        preferredName={call.person?.preferredName}
        locationId={call.locationId}
        key={call.person?.id}
        openProfileOnClick={false}
        phoneNumber={CallIntelUtils.getPhoneNumber(call.phoneNumber)}
        rightElement={<Text size='small'> {t(`{{ count }} Task`, { count: totalTasks })} </Text>}
        showOnlyName
        userId={call.person?.id}
      />
      <CallIntelChip dataKey={contactType} chipVariants={chipVariants} label={contactTypeLabelMap?.[contactType]} />
    </NakedButton>
  );
};

type TaskReasonCardProps = {
  count: number;
  isLoading: boolean;
  label: string;
  type: CallIntelTypes.TaskType;
};

// Task Reason Card Component
export const TaskReasonCard = ({ count, isLoading, label, type }: TaskReasonCardProps) => {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const alert = useAlert();
  const isSmallScreen = useIsMobile();

  const { headerChipVariants } = useCallIntelDefaultsShallowStore('headerChipVariants');
  const { setSubView } = useCallIntelShallowStore('setSubView');
  const { filters } = useActiveFilters();

  const { inView: isVisible, ref } = useInView({ triggerOnce: true });

  const followupTaskTypeFilters: CallIntelTypes.FollowupTaskTypeFilters = useMemo(
    () => ({
      startDate: filters.startDate,
      endDate: filters.endDate,
      locations: filters.locations,
      officeUsers: filters.officeUsers,
      officeHours: filters.officeHours,
      contactTypes: filters.contactTypes,
      taskTypes: [type],
      taskStatus: [],
    }),
    [filters, type]
  );

  const { variantColor: background, textColor: color } = headerChipVariants[type];

  const { data, isLoading: isPatientLoading } = CallIntelligenceV2.Queries.useListFollowupsQuery({
    request: {
      ...CallIntelUtils.generateFollowupsFilterPayload(followupTaskTypeFilters),
      limit: DEFAULT_PAGE_CONFIG.pageSize,
      offset: DEFAULT_PAGE_CONFIG.pageNumber - 1,
      sortBy: CallIntelTypes.FollowupsSortBy.FOLLOWUPS_SORT_LAST_CALL,
      sortType: CallIntelTypes.SortType.SORT_DESC,
    },
    options: {
      enabled: isVisible,
      retry: false,
      onError: () => {
        alert.error(t('Failed to fetch follow-ups'));
      },
    },
  });

  const followUps = useMemo(() => {
    return (
      data?.pages
        .flatMap((page) => page.followUps)
        .filter((followUp) => followUp?.calls?.length)
        .slice(0, DEFAULT_PAGE_CONFIG.pageSize) || []
    );
  }, [data]);

  const handleNavigation = useCallback(
    (route: string, searchParams: Record<string, string> = {}) => {
      navigate({ to: `${URLs.CALL_INTEL_BASE}${route}`, search: searchParams });
    },
    [navigate]
  );

  return (
    <div css={styles.taskReasonCardWrapper} ref={ref} data-testid={`task-reason-card-${type}`}>
      {isLoading ? (
        <TaskReasonCardLoader type='header' />
      ) : (
        <TaskReasonCardHeading
          background={background ?? theme.colors.neutral5}
          color={color ?? theme.colors.neutral50}
          count={count}
          label={label}
          infoTip={<CallIntelTooltip tip={TaskTypeInfoTipMapping[type]} />}
          onNavigate={() => {
            const slug = type.split(TASK_TYPE_PREFIX)[1]?.toLowerCase();
            setSubView({ id: slug, type: 'follow-up' });
            handleNavigation(`/follow-ups/${slug}`);
          }}
        />
      )}
      {!isSmallScreen && (
        <>
          {!isLoading && !isPatientLoading && <div css={styles.taskReasonDivider} />}

          {isLoading || isPatientLoading ? (
            <TaskReasonCardLoader type='body' />
          ) : (followUps?.length ?? 0) > 0 ? (
            <div css={styles.followUpsContainer} data-testid='follow-ups-list'>
              {followUps?.map((followUp) => {
                const call = followUp?.calls?.[0];
                if (!call) {
                  return null;
                }
                return (
                  <TaskPatientPreview
                    call={call}
                    contactType={call.contactType}
                    key={call.id}
                    onNavigate={(id) => {
                      const slug = type.split(TASK_TYPE_PREFIX)[1]?.toLowerCase();
                      setSubView({ id: slug, type: 'follow-up' });
                      handleNavigation(`/follow-ups/${type.split(TASK_TYPE_PREFIX)[1]?.toLowerCase()}`, { id });
                    }}
                    totalTasks={followUp?.totalTasks ?? 0}
                  />
                );
              })}
            </div>
          ) : (
            <EmptyTask typeLabel={label} />
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  emptyStateText: css`
    color: ${theme.colors.neutral50};
  `,
  emptyStateWrapper: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: ${theme.spacing(0, 2)};
  `,
  taskReasonCardHeadingWrapper: (background: string, color: string) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${theme.spacing(1, 0.5)};
    background: ${background};
    border-radius: ${theme.borderRadius.small};

    .info-wrapper {
      display: flex;
      align-items: center;

      .info-count {
        color: ${color};
        margin-right: ${theme.spacing(0.5)};
        font-size: ${theme.font.size.h3};
        line-height: ${theme.spacing(3)};
      }
    }
  `,
  taskPatientPreviewWrapper: css`
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    padding: ${theme.spacing(0.75, 0.5, 0.75, 1)};
    align-items: center;
    gap: ${theme.spacing(1)};
    flex: 1;

    :hover {
      background-color: ${theme.colors.neutral5};
    }
  `,
  userCard: css`
    display: flex;
    flex: 1;
    > div {
      flex: 1;
      cursor: pointer !important;

      > figure {
        height: ${theme.spacing(4)};
        width: ${theme.spacing(4)};
        min-height: ${theme.spacing(4)};
        min-width: ${theme.spacing(4)};
        font-size: ${theme.font.size.small};
      }

      .content-wrapper {
        display: flex;
        justify-content: space-between;
        flex: 1;
      }

      .name-wrapper {
        display: flex;
        flex: 1;
        flex-direction: column;
        align-items: flex-start;
        gap: ${theme.spacing(0.5)};
        max-width: 140px;

        > p {
          font-size: ${theme.font.size.medium};
          line-height: ${theme.spacing(2.5)};
        }

        > p:last-child {
          font-size: ${theme.font.size.small};
          line-height: ${theme.spacing(2)};
          color: ${theme.colors.neutral50};
        }

        span {
          font-size: ${theme.font.size.medium};
          line-height: ${theme.spacing(2.5)};
        }
      }
    }
  `,
  taskReasonCardWrapper: css`
    padding: ${theme.spacing(2)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    border: ${theme.spacing(0.1)} solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.medium};
    height: 272px;
    flex-shrink: 0;

    @media (max-width: ${breakpoints.small.max}px) {
      height: auto;
      padding: ${theme.spacing(1)};
    }
  `,
  taskReasonDivider: css`
    height: ${theme.spacing(0.125)};
    background-color: ${theme.colors.neutral20};
  `,
  followUpsContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0)};
  `,
};
